/**
 * Email input with autocomplete against the known-user directory
 * (`GET /api/users`). Suggestions show name + email; picking one fills the
 * email. Free typing is preserved — an email that isn't in the directory yet
 * (no IDP sync, never-signed-in) still submits fine.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';
import type { UserSummary } from '../api/types.js';
import { Avatar } from './Avatar.js';
import { inputClass } from './ui.js';

interface UserComboboxProps {
  value: string;
  onChange: (email: string) => void;
  placeholder?: string;
  required?: boolean;
}

export function UserCombobox({ value, onChange, placeholder, required }: UserComboboxProps) {
  const [results, setResults] = useState<UserSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = value.trim();
    if (q.length === 0) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get<{ users: UserSummary[] }>(`/api/users?q=${encodeURIComponent(q)}`)
        .then((res) => {
          if (!cancelled) setResults(res.users);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function pick(user: UserSummary) {
    onChange(user.email);
    setOpen(false);
    setHighlight(-1);
  }

  const showDropdown = open && results.length > 0;

  return (
    <div className="relative" ref={ref}>
      <input
        type="email"
        required={required}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!showDropdown) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter' && highlight >= 0) {
            e.preventDefault();
            pick(results[highlight]!);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={inputClass}
      />
      {showDropdown && (
        <ul className="absolute z-20 mt-1.5 w-full max-h-56 overflow-auto rounded-lg border border-line bg-surface shadow-elevated animate-fade-in">
          {results.map((u, i) => (
            <li key={u.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(u)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors inline-flex items-center gap-2 ${
                  i === highlight ? 'bg-brand-soft text-ink' : 'hover:bg-surface-2 text-ink'
                }`}
              >
                <Avatar user={u} size={20} />
                <span className="min-w-0 truncate">
                  <span className="font-medium">{u.name ?? u.email}</span>
                  {u.name && <span className="text-muted ml-2">{u.email}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
